import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Auth from '../components/Auth/Auth';
import { TicketType as TicketCategory } from '../constants/tickets';
import { TicketType } from '../types/ticket';
import { useBreadCrumbsContext } from './contexts/breadCrumb.context';
import { useCartContext } from './contexts/cart.context';
import { useEventContext } from './contexts/event.context';
import { useI18nContext } from './contexts/i18n.context';
import { useModalContext } from './contexts/modal.context';
import { useUserContext } from './contexts/user.context';

type SafeClickAddTicketToCart = {
  ticket: TicketType;
};

const useGuardian = () => {
  const { translate } = useI18nContext();
  const { pathname, search } = useLocation();
  const { event } = useEventContext();
  const { validateCart } = useCartContext();
  const { authenticatedUser, user } = useUserContext();
  const { breadCrumbs, currentBreadCrumb, isTicketPage } =
    useBreadCrumbsContext();
  const { defineTitleModal, setContentModal, toggleModal } = useModalContext();

  const navigate = useNavigate();

  const safeClickIfNeedLogin = ({
    ticket,
  }: SafeClickAddTicketToCart): boolean => {
    if (
      +ticket.category.id === TicketCategory.COMPANION
      && !authenticatedUser
    ) {
      defineTitleModal(translate(['INFORMATION', 'IDENTIFY_YOURSELF']));
      setContentModal(<Auth path="login" />);
      toggleModal({ open : true });
      return false;
    }
    return true;
  };

  const getNextStep = (): string => {
    const nextStep =
      breadCrumbs.findIndex((crumb) => crumb.route === currentBreadCrumb) + 1;
    const step = breadCrumbs[nextStep] || breadCrumbs[0];
    return step.route;
  };

  /**
   * Replaces the first route in a given URL with a new route.
   * @param {string} url - The original URL.
   * @param {string} newRoute - The new route to replace the first route.
   * @returns {string} The updated URL with the first route replaced.
   */
  const replaceFirstRoute = (url: string, newRoute: string): string => {
    return url.replace(/^\/[^/]+/, `/${newRoute}`);
  };

  /**
   * Extracts the first route segment from the given URL path.
   * @param {string} url - The URL path.
   * @returns {string} The first route segment.
   */
  const extractFirstRouteSegment = (url: string): string => {
    const segments = url.split('/');
    return segments[1];
  };

  /**
   * Renders the given children if certain conditions are met.
   * If there are breadcrumbs and the user is not logged in, it returns null.
   * Otherwise, it renders the children.
   * @function render
   * @param {React.ReactNode} children - The child components to render.
   * @returns React.ReactNode | null The rendered children or null.
   */
  function render(children: React.ReactNode) {
    if (breadCrumbs.length > 0 && !authenticatedUser) {
      return null;
    }
    return children;
  }

  /**
   * Navigates to a specified path, handling protected routes and authentication.
   * If the path requires authentication, it opens the login modal and redirects the user after login.
   * If the path does not require authentication, it redirects the user immediately.
   * @function navigateToNextRoute
   * @param {Object} params - The parameters for navigation.
   * @param {string} params.path - The path to navigate to.
   * @throws {Error} Throws an error if the specified route does not exist.
   */
  const navigateToNextRoute = async ({ route }: { route?: string } = {}) => {
    const path = getNextStep();
    const to = route || path;
    const cachedRoute = replaceFirstRoute(`${pathname}${search}`, to);
    const crumb = breadCrumbs.find((crumb) => crumb.route === path);

    if (!crumb && !route) throw new Error(`The route ${path} does not exist`);

    const isProtected = crumb ? crumb.private : false;

    if ((isProtected && !authenticatedUser) || isTicketPage) {
      defineTitleModal(translate(['INFORMATION', 'IDENTIFY_YOURSELF']));
      setContentModal(
        <Auth
          validateCart={validateCart}
          cachedRoute={cachedRoute}
          path="login"
          isTicketPage={isTicketPage}
        />
      );
      setTimeout(() => toggleModal({ open : true }), 100);
      return;
    }

    if (isTicketPage && user && event && validateCart) {
      toggleModal({ open : false });
      const isValid = await validateCart({ user, event, navigate });
      if (!isValid) return;
    }

    navigate(cachedRoute, { replace : false, state : { from : null } });
  };

  /**
   * Navigates to the first route in the breadcrumb trail.
   * This function constructs a new path by replacing the first route in the current
   * pathname and search parameters, then navigates to this new path.
   * @function navigateToFirstRoute
   * @returns void
   */
  const navigateToFirstRoute = () => {
    const firstRoute = breadCrumbs[0].route;
    const newPath = replaceFirstRoute(`${pathname}${search}`, firstRoute);
    navigate(newPath, { replace : false, state : { from : null } });
  };

  /**
   * Effect that navigates to a new path if breadcrumbs exist and the user is not logged in.
   * This effect is triggered whenever the `breadCrumbs`, `user`, `pathname`, `search`, or `navigate` dependencies change.
   * @useEffect
   */
  useEffect(() => {
    if (breadCrumbs.length > 0 && !authenticatedUser) {
      const cachedRoute = `${pathname}${search}`;
      const path = extractFirstRouteSegment(cachedRoute);
      const crumb = breadCrumbs.find((crumb) => crumb.route === path);

      // if (!crumb) return navigateToFirstRoute();

      if (crumb?.private) {
        const firstRoute = breadCrumbs[0].route;
        const newPath = replaceFirstRoute(cachedRoute, firstRoute);
        navigate(newPath, { replace : false, state : { from : null } });
      }
    }
  }, [breadCrumbs, authenticatedUser, pathname, search, navigate]);

  return {
    render,
    navigateToNextRoute,
    safeClickIfNeedLogin,
    navigateToFirstRoute,
  };
};

export default useGuardian;
